import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ticketService } from "@/services/ticketService";
import { userService } from "@/services/userService";
import { bulkService } from "@/services/bulkService";
import { savedViewService } from "@/services/savedViewService";
import { formatError } from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge, PriorityBadge, SLABadge, STATUS_OPTIONS, PRIORITY_OPTIONS, CATEGORY_OPTIONS, MODULE_OPTIONS } from "@/utils/constants";
import { DateRangePicker } from "@/components/DateRangePicker";
import { formatDate } from "@/utils/formatters";
import { toast } from "sonner";
import { Search, Plus, ChevronLeft, ChevronRight, X, UserPlus, XCircle, Bookmark, BookmarkCheck, Save, Pencil, Ticket } from "lucide-react";

const VALID_TRANSITIONS = {
  open: ["assigned", "in_progress"], assigned: ["in_progress", "on_hold"],
  in_progress: ["on_hold", "resolved"], on_hold: ["in_progress", "assigned"],
  resolved: ["closed", "reopened"], closed: ["reopened"], reopened: ["assigned", "in_progress"],
};
const STATUS_LABELS = { open: "Open", assigned: "Assigned", in_progress: "In Progress", on_hold: "On Hold", resolved: "Resolved", closed: "Closed", reopened: "Reopened" };

function getWorstSla(sla) {
  if (!sla) return null;
  if (sla.resolution === "breached" || sla.response === "breached") return "breached";
  if (sla.resolution === "at_risk" || sla.response === "at_risk") return "at_risk";
  return "on_track";
}

export default function TicketListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "", priority: "", category: "", module: "", assigned_to: "", search: "" });
  const [searchInput, setSearchInput] = useState("");
  const [dateRange, setDateRange] = useState(undefined);

  const [selectedIds, setSelectedIds] = useState([]);
  const [agents, setAgents] = useState([]);
  const [bulkAssignee, setBulkAssignee] = useState("");
  const [bulkProcessing, setBulkProcessing] = useState(false);

  const [savedViews, setSavedViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [viewName, setViewName] = useState("");
  const [viewIsDefault, setViewIsDefault] = useState(false);

  const [inlineEdit, setInlineEdit] = useState(null);
  const [inlineValue, setInlineValue] = useState("");
  const [inlineSaving, setInlineSaving] = useState(false);
  const [inlineStatusComment, setInlineStatusComment] = useState("");
  const [inlineStatusDialogOpen, setInlineStatusDialogOpen] = useState(false);

  const canBulk = user?.role === "admin" || user?.role === "manager";
  const canInlineEdit = user?.role !== "end_user";

  // eslint-disable-next-line react-hooks/exhaustive-deps -- service imports and setState are stable references
  const fetchTickets = useCallback(async () => {
    setLoading(true);
    const params = { page, limit: 15 };
    if (filters.status) params.status = filters.status;
    if (filters.priority) params.priority = filters.priority;
    if (filters.category) params.category = filters.category;
    if (filters.module) params.module = filters.module;
    if (filters.assigned_to) params.assigned_to = filters.assigned_to;
    if (filters.search) params.search = filters.search;
    if (dateRange?.from) params.date_from = new Date(dateRange.from.getFullYear(), dateRange.from.getMonth(), dateRange.from.getDate()).toISOString();
    if (dateRange?.to) params.date_to = new Date(dateRange.to.getFullYear(), dateRange.to.getMonth(), dateRange.to.getDate(), 23, 59, 59).toISOString();
    try {
      const { data } = await ticketService.list(params);
      setTickets(data.tickets); setTotal(data.total); setPages(data.pages);
    } catch { toast.error("Failed to load tickets"); } finally { setLoading(false); }
  }, [page, filters, dateRange]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- userService is a stable import, runs once per role change
  useEffect(() => { if (user?.role !== "end_user") { userService.listAgents().then(({ data }) => setAgents(data)).catch(() => {}); } }, [user?.role]);
  useEffect(() => {
    savedViewService.list().then(({ data }) => {
      setSavedViews(data);
      const defaultView = data.find((v) => v.is_default);
      if (defaultView) applyView(defaultView);
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { setSelectedIds([]); }, [page, filters]);

  const handleSearch = (e) => { e.preventDefault(); setPage(1); setFilters((f) => ({ ...f, search: searchInput })); setActiveViewId(null); };
  const clearFilters = () => { setFilters({ status: "", priority: "", category: "", module: "", assigned_to: "", search: "" }); setSearchInput(""); setDateRange(undefined); setPage(1); setActiveViewId(null); };
  const hasFilters = filters.status || filters.priority || filters.category || filters.module || filters.assigned_to || filters.search || dateRange?.from;

  const applyView = (view) => {
    const f = view.filters || {};
    setFilters({ status: f.status || "", priority: f.priority || "", search: f.search || "" });
    setSearchInput(f.search || "");
    setDateRange(f.date_from && f.date_to ? { from: new Date(f.date_from), to: new Date(f.date_to) } : undefined);
    setPage(1); setActiveViewId(view.id);
  };

  const handleSaveView = async () => {
    if (!viewName.trim()) { toast.error("View name required"); return; }
    const viewFilters = { ...filters, date_from: dateRange?.from ? dateRange.from.toISOString() : null, date_to: dateRange?.to ? dateRange.to.toISOString() : null };
    try {
      const { data } = await savedViewService.create({ name: viewName.trim(), filters: viewFilters, is_default: viewIsDefault });
      setSavedViews((prev) => viewIsDefault ? [...prev.map((v) => ({ ...v, is_default: false })), data] : [...prev, data]);
      setActiveViewId(data.id); toast.success(`View "${data.name}" saved`);
      setSaveDialogOpen(false); setViewName(""); setViewIsDefault(false);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const handleDeleteView = async (viewId) => {
    try { await savedViewService.delete(viewId); setSavedViews((prev) => prev.filter((v) => v.id !== viewId)); if (activeViewId === viewId) setActiveViewId(null); toast.success("View deleted"); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const toggleSelect = (id) => setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const toggleAll = () => selectedIds.length === tickets.length ? setSelectedIds([]) : setSelectedIds(tickets.map((t) => t.id));
  const startInlineEdit = (ticketId, field, currentValue) => { setInlineEdit({ ticketId, field }); setInlineValue(currentValue || ""); };
  const cancelInlineEdit = () => { setInlineEdit(null); setInlineValue(""); };

  const saveInlineEdit = async () => {
    if (!inlineEdit || inlineSaving) return;
    const { ticketId, field } = inlineEdit;
    if (field === "status") {
      // Open comment dialog for status changes
      setInlineStatusDialogOpen(true);
      return;
    }
    setInlineSaving(true);
    try {
      await ticketService.update(ticketId, { [field]: inlineValue });
      toast.success("Updated"); cancelInlineEdit(); fetchTickets();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setInlineSaving(false); }
  };

  const confirmInlineStatusChange = async () => {
    if (!inlineStatusComment.trim()) { toast.error("Comment is required for status changes"); return; }
    setInlineSaving(true);
    try {
      await ticketService.updateStatus(inlineEdit.ticketId, inlineValue, inlineStatusComment.trim());
      toast.success("Status updated"); setInlineStatusDialogOpen(false); setInlineStatusComment(""); cancelInlineEdit(); fetchTickets();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setInlineSaving(false); }
  };

  const handleBulkAssign = async () => {
    if (!bulkAssignee || selectedIds.length === 0) return;
    setBulkProcessing(true);
    try { const { data } = await bulkService.assign({ ticket_ids: selectedIds, assigned_to: bulkAssignee }); toast.success(`${data.updated} ticket(s) assigned`); setSelectedIds([]); setBulkAssignee(""); fetchTickets(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setBulkProcessing(false); }
  };

  const handleBulkClose = async () => {
    if (selectedIds.length === 0 || !window.confirm(`Close ${selectedIds.length} ticket(s)?`)) return;
    setBulkProcessing(true);
    try { const { data } = await bulkService.close({ ticket_ids: selectedIds }); toast.success(`${data.updated} ticket(s) closed`); setSelectedIds([]); fetchTickets(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setBulkProcessing(false); }
  };

  return (
    <div className="p-6 space-y-4" data-testid="ticket-list-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-[#09090B]">Tickets</h1>
          <p className="text-sm text-[#52525B] mt-0.5">{total} ticket{total !== 1 ? "s" : ""} {user?.role === "agent" && !filters.search ? "assigned to you or created by you" : "total"}</p>
        </div>
        <Button onClick={() => navigate("/tickets/new")} data-testid="create-ticket-button" className="rounded-sm bg-[#003DA5] hover:bg-[#002D7A] text-white gap-1.5"><Plus size={15} /> New Ticket</Button>
      </div>

      <SavedViewsBar views={savedViews} activeViewId={activeViewId} onApply={applyView} onDelete={handleDeleteView} />

      <FilterBar searchInput={searchInput} setSearchInput={setSearchInput} filters={filters} setFilters={setFilters}
        dateRange={dateRange} setDateRange={setDateRange} hasFilters={hasFilters} clearFilters={clearFilters}
        handleSearch={handleSearch} setPage={setPage} setActiveViewId={setActiveViewId}
        onOpenSaveDialog={() => { setViewName(""); setViewIsDefault(false); setSaveDialogOpen(true); }}
        agents={agents} userRole={user?.role} />

      {canBulk && selectedIds.length > 0 && (
        <BulkActionBar selectedIds={selectedIds} agents={agents} bulkAssignee={bulkAssignee} setBulkAssignee={setBulkAssignee}
          bulkProcessing={bulkProcessing} handleBulkAssign={handleBulkAssign} handleBulkClose={handleBulkClose} clearSelection={() => setSelectedIds([])} />
      )}

      <TicketTable loading={loading} tickets={tickets} canBulk={canBulk} canInlineEdit={canInlineEdit} selectedIds={selectedIds}
        toggleSelect={toggleSelect} toggleAll={toggleAll} navigate={navigate} agents={agents}
        inlineEdit={inlineEdit} inlineValue={inlineValue} inlineSaving={inlineSaving}
        startInlineEdit={startInlineEdit} setInlineValue={setInlineValue} saveInlineEdit={saveInlineEdit} cancelInlineEdit={cancelInlineEdit}
        page={page} pages={pages} setPage={setPage} />

      <SaveViewDialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen} viewName={viewName} setViewName={setViewName}
        viewIsDefault={viewIsDefault} setViewIsDefault={setViewIsDefault} handleSave={handleSaveView} />

      {/* Inline Status Change Comment Dialog */}
      <Dialog open={inlineStatusDialogOpen} onOpenChange={(open) => { if (!open) { setInlineStatusDialogOpen(false); setInlineStatusComment(""); cancelInlineEdit(); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Status Change Comment</DialogTitle></DialogHeader>
          <p className="text-sm text-[#52525B]">Please provide a reason for this status change.</p>
          <Textarea value={inlineStatusComment} onChange={(e) => setInlineStatusComment(e.target.value)} placeholder="e.g., Starting work on this ticket..." className="rounded-sm min-h-[80px] text-sm" data-testid="inline-status-comment-input" autoFocus />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setInlineStatusDialogOpen(false); setInlineStatusComment(""); cancelInlineEdit(); }} className="rounded-sm">Cancel</Button>
            <Button onClick={confirmInlineStatusChange} disabled={inlineSaving || !inlineStatusComment.trim()} className="rounded-sm bg-[#003DA5] hover:bg-[#002D7A] text-white" data-testid="confirm-inline-status-btn">{inlineSaving ? "Saving..." : "Confirm"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ==================== SUB-COMPONENTS ==================== */

function SavedViewsBar({ views, activeViewId, onApply, onDelete }) {
  if (views.length === 0) return null;
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="saved-views-bar">
      <Bookmark size={14} className="text-[#52525B]" />
      {views.map((v) => (
        <div key={v.id} className="flex items-center gap-0.5">
          <Button variant={activeViewId === v.id ? "default" : "outline"} size="sm" onClick={() => onApply(v)}
            className={`rounded-sm text-xs h-7 ${activeViewId === v.id ? "bg-[#003DA5] text-white hover:bg-[#002D7A]" : ""}`} data-testid={`view-${v.id}`}>
            {v.is_default && <BookmarkCheck size={11} className="mr-1" />}{v.name}
          </Button>
          <button onClick={() => onDelete(v.id)} className="text-[#52525B] hover:text-red-500 p-0.5" data-testid={`delete-view-${v.id}`}><X size={11} /></button>
        </div>
      ))}
    </div>
  );
}

function FilterBar({ searchInput, setSearchInput, filters, setFilters, dateRange, setDateRange, hasFilters, clearFilters, handleSearch, setPage, setActiveViewId, onOpenSaveDialog, agents, userRole }) {
  const updateFilter = (key, v) => { setPage(1); setFilters((f) => ({ ...f, [key]: v === "all" ? "" : v })); setActiveViewId(null); };
  return (
    <Card className="border-[#E4E4E7] shadow-none rounded-sm p-3">
      <div className="flex flex-wrap items-center gap-2">
        <form onSubmit={handleSearch} className="flex items-center gap-1.5 flex-1 min-w-[200px]">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#52525B]" />
            <Input data-testid="ticket-search-input" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search tickets..." className="pl-8 h-8 rounded-sm border-zinc-300 text-sm" />
          </div>
        </form>
        <Select value={filters.status || "all"} onValueChange={(v) => updateFilter("status", v)}>
          <SelectTrigger className="w-[130px] h-8 rounded-sm text-sm" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Statuses</SelectItem>{STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.priority || "all"} onValueChange={(v) => updateFilter("priority", v)}>
          <SelectTrigger className="w-[120px] h-8 rounded-sm text-sm" data-testid="filter-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Priorities</SelectItem>{PRIORITY_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.category || "all"} onValueChange={(v) => updateFilter("category", v)}>
          <SelectTrigger className="w-[130px] h-8 rounded-sm text-sm" data-testid="filter-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Categories</SelectItem>{CATEGORY_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.module || "all"} onValueChange={(v) => updateFilter("module", v)}>
          <SelectTrigger className="w-[120px] h-8 rounded-sm text-sm" data-testid="filter-module"><SelectValue placeholder="Module" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Modules</SelectItem>{MODULE_OPTIONS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
        </Select>
        {userRole !== "end_user" && (
          <Select value={filters.assigned_to || "all"} onValueChange={(v) => updateFilter("assigned_to", v)}>
            <SelectTrigger className="w-[130px] h-8 rounded-sm text-sm" data-testid="filter-assignee"><SelectValue placeholder="Assignee" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Assignees</SelectItem>{agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
        <DateRangePicker dateRange={dateRange} onDateRangeChange={(r) => { setPage(1); setDateRange(r); setActiveViewId(null); }} />
        {hasFilters && (
          <>
            <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 text-xs gap-1" data-testid="clear-filters"><X size={12} /> Clear</Button>
            <Button variant="outline" size="sm" onClick={onOpenSaveDialog} className="h-8 text-xs gap-1" data-testid="save-view-btn"><Save size={12} /> Save View</Button>
          </>
        )}
      </div>
    </Card>
  );
}

function BulkActionBar({ selectedIds, agents, bulkAssignee, setBulkAssignee, bulkProcessing, handleBulkAssign, handleBulkClose, clearSelection }) {
  return (
    <Card className="border-[#003DA5] bg-[#F0F4FF] shadow-none rounded-sm p-3" data-testid="bulk-action-bar">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-[#003DA5]" data-testid="bulk-count">{selectedIds.length} selected</span>
        <div className="flex items-center gap-1.5">
          <Select value={bulkAssignee || "pick"} onValueChange={(v) => setBulkAssignee(v === "pick" ? "" : v)}>
            <SelectTrigger className="h-7 w-[150px] rounded-sm text-xs" data-testid="bulk-assignee-select"><SelectValue placeholder="Assign to..." /></SelectTrigger>
            <SelectContent><SelectItem value="pick" disabled>Assign to...</SelectItem>{agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={handleBulkAssign} disabled={!bulkAssignee || bulkProcessing} className="rounded-sm text-xs h-7 gap-1" data-testid="bulk-assign-btn"><UserPlus size={12} /> Assign</Button>
        </div>
        <Button variant="outline" size="sm" onClick={handleBulkClose} disabled={bulkProcessing} className="rounded-sm text-xs h-7 gap-1 text-red-600 border-red-200 hover:bg-red-50" data-testid="bulk-close-btn"><XCircle size={12} /> Close</Button>
        <Button variant="ghost" size="sm" onClick={clearSelection} className="rounded-sm text-xs h-7 ml-auto" data-testid="bulk-clear-btn">Clear Selection</Button>
      </div>
    </Card>
  );
}

function TicketTable({ loading, tickets, canBulk, canInlineEdit, selectedIds, toggleSelect, toggleAll, navigate, agents,
  inlineEdit, inlineValue, inlineSaving, startInlineEdit, setInlineValue, saveInlineEdit, cancelInlineEdit, page, pages, setPage }) {
  const colCount = canBulk ? 8 : 7;
  return (
    <Card className="border-[#E4E4E7] shadow-none rounded-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="tickets-table">
          <thead>
            <tr className="border-b border-[#E4E4E7] bg-[#F4F4F5]">
              {canBulk && <th className="w-10 px-3 py-2"><Checkbox checked={tickets.length > 0 && selectedIds.length === tickets.length} onCheckedChange={toggleAll} data-testid="select-all-checkbox" /></th>}
              <th className="text-left px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">Ticket</th>
              <th className="text-left px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">Title</th>
              <th className="text-left px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">Status</th>
              <th className="text-left px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">Priority</th>
              <th className="text-left px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">SLA</th>
              <th className="text-left px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">Assignee</th>
              <th className="text-left px-3 py-2 text-xs uppercase tracking-[0.1em] font-bold text-[#52525B]">Created</th>
            </tr>
          </thead>
          <tbody>
            <TableBody loading={loading} tickets={tickets} colCount={colCount} canBulk={canBulk} canInlineEdit={canInlineEdit}
              selectedIds={selectedIds} toggleSelect={toggleSelect} navigate={navigate} agents={agents}
              inlineEdit={inlineEdit} inlineValue={inlineValue} inlineSaving={inlineSaving}
              startInlineEdit={startInlineEdit} setInlineValue={setInlineValue} saveInlineEdit={saveInlineEdit} cancelInlineEdit={cancelInlineEdit} />
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="flex items-center justify-between px-3 py-2 border-t border-[#E4E4E7] bg-[#F4F4F5]">
          <p className="text-xs text-[#52525B]">Page {page} of {pages}</p>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="h-7 rounded-sm" data-testid="prev-page"><ChevronLeft size={14} /></Button>
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page === pages} className="h-7 rounded-sm" data-testid="next-page"><ChevronRight size={14} /></Button>
          </div>
        </div>
      )}
    </Card>
  );
}

function TableBody({ loading, tickets, colCount, canBulk, canInlineEdit, selectedIds, toggleSelect, navigate, agents,
  inlineEdit, inlineValue, inlineSaving, startInlineEdit, setInlineValue, saveInlineEdit, cancelInlineEdit }) {
  if (loading) {
    return [1,2,3,4,5].map((sk) => (
      <tr key={sk} className="border-b border-[#F4F4F5]">{Array.from({ length: colCount }, (_, i) => <td key={i} className="px-3 py-3"><div className="h-4 bg-zinc-100 rounded animate-pulse" /></td>)}</tr>
    ));
  }
  if (tickets.length === 0) {
    return (
      <tr><td colSpan={colCount} className="px-3 py-16 text-center" data-testid="no-tickets">
        <Ticket size={32} className="mx-auto text-[#D4D4D8] mb-2" />
        <p className="text-sm text-[#71717A]">No tickets found</p>
        <p className="text-xs text-[#A1A1AA] mt-0.5">Try adjusting your filters or create a new ticket</p>
      </td></tr>
    );
  }
  return tickets.map((ticket) => (
    <TicketRow key={ticket.id} ticket={ticket} canBulk={canBulk} canInlineEdit={canInlineEdit}
      isSelected={selectedIds.includes(ticket.id)} toggleSelect={toggleSelect} navigate={navigate} agents={agents}
      inlineEdit={inlineEdit} inlineValue={inlineValue} inlineSaving={inlineSaving}
      startInlineEdit={startInlineEdit} setInlineValue={setInlineValue} saveInlineEdit={saveInlineEdit} cancelInlineEdit={cancelInlineEdit} />
  ));
}

function TicketRow({ ticket, canBulk, canInlineEdit, isSelected, toggleSelect, navigate, agents,
  inlineEdit, inlineValue, inlineSaving, startInlineEdit, setInlineValue, saveInlineEdit, cancelInlineEdit }) {
  const worstSla = getWorstSla(ticket.sla_status);
  return (
    <tr className={`border-b border-[#F4F4F5] hover:bg-[#F8FAFC] ${isSelected ? "bg-blue-50/40" : ""}`} data-testid={`ticket-row-${ticket.ticket_number}`}>
      {canBulk && <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}><Checkbox checked={isSelected} onCheckedChange={() => toggleSelect(ticket.id)} data-testid={`select-${ticket.ticket_number}`} /></td>}
      <td className="px-3 py-3 font-mono text-xs text-[#71717A] cursor-pointer" onClick={() => navigate(`/tickets/${ticket.id}`)}>{ticket.ticket_number}</td>
      <td className="px-3 py-3 max-w-[300px] cursor-pointer hover:text-[#003DA5]" onClick={() => navigate(`/tickets/${ticket.id}`)}>
        <span className="text-[#09090B] font-medium truncate block">{ticket.title}</span>
        <span className="text-[10px] text-[#71717A] mt-0.5 flex gap-2">
          {ticket.category && <span className="capitalize">{ticket.category.replace(/_/g, " ")}</span>}
          {ticket.module && <span>{MODULE_OPTIONS.find((m) => m.value === ticket.module)?.label || ticket.module}</span>}
        </span>
      </td>
      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
        {canInlineEdit ? (
          <InlineStatusEditor ticket={ticket} inlineEdit={inlineEdit} inlineValue={inlineValue} inlineSaving={inlineSaving} startInlineEdit={startInlineEdit} setInlineValue={setInlineValue} saveInlineEdit={saveInlineEdit} cancelInlineEdit={cancelInlineEdit} />
        ) : <span className="cursor-pointer" onClick={() => navigate(`/tickets/${ticket.id}`)}><StatusBadge status={ticket.status} /></span>}
      </td>
      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
        {canInlineEdit ? (
          <InlineSelectEditor ticket={ticket} field="priority" options={PRIORITY_OPTIONS} currentValue={ticket.priority} inlineEdit={inlineEdit} inlineValue={inlineValue} inlineSaving={inlineSaving} startInlineEdit={startInlineEdit} setInlineValue={setInlineValue} saveInlineEdit={saveInlineEdit} cancelInlineEdit={cancelInlineEdit} renderBadge={() => <PriorityBadge priority={ticket.priority} />} />
        ) : <span className="cursor-pointer" onClick={() => navigate(`/tickets/${ticket.id}`)}><PriorityBadge priority={ticket.priority} /></span>}
      </td>
      <td className="px-3 py-3 cursor-pointer" onClick={() => navigate(`/tickets/${ticket.id}`)}>{worstSla && <SLABadge type="resolution" status={worstSla} />}</td>
      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
        {canInlineEdit ? (
          <InlineSelectEditor ticket={ticket} field="assigned_to" options={agents.map((a) => ({ value: a.id, label: a.name }))} currentValue={ticket.assigned_to || ""} inlineEdit={inlineEdit} inlineValue={inlineValue} inlineSaving={inlineSaving} startInlineEdit={startInlineEdit} setInlineValue={setInlineValue} saveInlineEdit={saveInlineEdit} cancelInlineEdit={cancelInlineEdit} renderBadge={() => ticket.assigned_to_name ? <span className="text-sm text-[#09090B]">{ticket.assigned_to_name}</span> : <span className="text-xs text-[#A1A1AA] italic">Unassigned</span>} />
        ) : <span className="text-[#52525B] cursor-pointer" onClick={() => navigate(`/tickets/${ticket.id}`)}>{ticket.assigned_to_name || <span className="text-[#A1A1AA] italic text-xs">Unassigned</span>}</span>}
      </td>
      <td className="px-3 py-3 text-xs text-[#71717A] cursor-pointer" onClick={() => navigate(`/tickets/${ticket.id}`)}>{formatDate(ticket.created_at)}</td>
    </tr>
  );
}

function SaveViewDialog({ open, onOpenChange, viewName, setViewName, viewIsDefault, setViewIsDefault, handleSave }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle className="font-heading">Save Current View</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input value={viewName} onChange={(e) => setViewName(e.target.value)} placeholder="View name, e.g. My Open Critical" className="rounded-sm" data-testid="view-name-input" />
          <div className="flex items-center gap-2">
            <Checkbox id="view_default" checked={viewIsDefault} onCheckedChange={setViewIsDefault} data-testid="view-default-checkbox" />
            <label htmlFor="view_default" className="text-sm text-[#52525B]">Set as default view</label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={handleSave} className="rounded-sm bg-[#003DA5] hover:bg-[#002D7A] text-white" data-testid="confirm-save-view-btn">Save View</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InlineStatusEditor({ ticket, inlineEdit, inlineValue, inlineSaving, startInlineEdit, setInlineValue, saveInlineEdit, cancelInlineEdit }) {
  const isEditing = inlineEdit?.ticketId === ticket.id && inlineEdit?.field === "status";
  const transitions = VALID_TRANSITIONS[ticket.status] || [];

  if (!isEditing) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button className="group flex items-center gap-1" data-testid={`inline-status-${ticket.ticket_number}`}>
            <StatusBadge status={ticket.status} />
            <Pencil size={10} className="text-[#52525B] opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-1.5" align="start">
          <div className="flex flex-col gap-0.5">
            {transitions.length === 0 && <p className="text-xs text-[#52525B] px-2 py-1">No transitions available</p>}
            {transitions.map((s) => (
              <button key={s} onClick={() => { startInlineEdit(ticket.id, "status", s); setTimeout(() => setInlineValue(s), 0); }} className="text-left px-2 py-1 text-xs rounded-sm hover:bg-[#F4F4F5] capitalize" data-testid={`inline-status-opt-${s}`}>{STATUS_LABELS[s]}</button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <span className="text-xs capitalize">{STATUS_LABELS[inlineValue]}</span>
      <Button size="sm" onClick={saveInlineEdit} disabled={inlineSaving} className="h-5 w-5 p-0 rounded-sm bg-[#003DA5]" data-testid="inline-save"><Save size={10} className="text-white" /></Button>
      <Button variant="ghost" size="sm" onClick={cancelInlineEdit} className="h-5 w-5 p-0 rounded-sm"><X size={10} /></Button>
    </div>
  );
}

function InlineSelectEditor({ ticket, field, options, currentValue, inlineEdit, inlineValue, inlineSaving, startInlineEdit, setInlineValue, saveInlineEdit, cancelInlineEdit, renderBadge }) {
  const isEditing = inlineEdit?.ticketId === ticket.id && inlineEdit?.field === field;

  if (!isEditing) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button className="group flex items-center gap-1" data-testid={`inline-${field}-${ticket.ticket_number}`}>
            {renderBadge()}
            <Pencil size={10} className="text-[#52525B] opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-1.5" align="start">
          <div className="flex flex-col gap-0.5 max-h-[200px] overflow-y-auto">
            {options.map((opt) => (
              <button key={opt.value} onClick={() => { if (opt.value !== currentValue) { startInlineEdit(ticket.id, field, opt.value); setTimeout(() => setInlineValue(opt.value), 0); } }} className={`text-left px-2 py-1 text-xs rounded-sm hover:bg-[#F4F4F5] ${opt.value === currentValue ? "bg-[#F4F4F5] font-medium" : ""}`} data-testid={`inline-${field}-opt-${opt.value}`}>{opt.label}</button>
            ))}
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  const selectedLabel = options.find((o) => o.value === inlineValue)?.label || inlineValue;
  return (
    <div className="flex items-center gap-1">
      <span className="text-xs">{selectedLabel}</span>
      <Button size="sm" onClick={saveInlineEdit} disabled={inlineSaving} className="h-5 w-5 p-0 rounded-sm bg-[#003DA5]" data-testid="inline-save"><Save size={10} className="text-white" /></Button>
      <Button variant="ghost" size="sm" onClick={cancelInlineEdit} className="h-5 w-5 p-0 rounded-sm"><X size={10} /></Button>
    </div>
  );
}
